import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { HomeHeader } from '../../components/HomeHeader.js';
import { request } from '../../sync/api.js';

/**
 * Рабочий стол администратора: что требует внимания прямо сейчас.
 *
 * Смысл экрана — не меню, а сводка: сколько смен не сдано, сколько заявок ждёт
 * решения, есть ли срывы. Меню появляется как следствие.
 *
 * Это и есть главный экран администратора — отдельного «домой» у него нет.
 */

interface Missing {
  shiftId: string;
  siteName: string;
  machineName: string;
  date: string;
  shiftNumber: number;
}

interface Absence {
  id: string;
  status: string;
  type: string;
}

export function AdminHomeScreen(): JSX.Element {
  const [missing, setMissing] = useState<number | null>(null);
  const [pending, setPending] = useState<number | null>(null);
  const [unplanned, setUnplanned] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [missingResponse, absenceResponse] = await Promise.all([
          request<{ missing: Missing[] }>('/api/reports/missing', { method: 'GET' }, 20_000),
          request<{ absences: Absence[] }>('/api/absences', { method: 'GET' }, 20_000),
        ]);
        setMissing(missingResponse.missing.length);
        setPending(absenceResponse.absences.filter((item) => item.status === 'PENDING').length);
        setUnplanned(
          absenceResponse.absences.filter((item) => item.type === 'UNPLANNED').length,
        );
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить сводку');
      }
    })();
  }, []);

  const tile = (title: string, value: number | null, to: string, alarming: boolean): JSX.Element => (
    <Link
      to={to}
      className={`rounded-xl px-5 py-5 ${alarming && (value ?? 0) > 0 ? 'bg-rose-50 text-rose-800 ring-1 ring-rose-200' : 'bg-card shadow-card'}`}
    >
      <p className="text-display font-semibold tabular-nums">{value ?? '…'}</p>
      <p className="mt-1 text-base text-body">{title}</p>
    </Link>
  );

  return (
    <div className="min-h-screen px-4 py-6">
      <HomeHeader title="Личный кабинет" />

      {error && <p className="mb-4 text-base text-amber-700">{error}</p>}

      <div className="mb-6 grid grid-cols-2 gap-touch">
        {tile('Смен не сдано', missing, '/admin/missing', true)}
        {tile('Заявок ждут решения', pending, '/admin/absences', false)}
        {tile('Срывов смен', unplanned, '/admin/absences', true)}
      </div>

      <nav className="flex flex-col gap-touch">
        <Link
          to="/admin/reports"
          className="rounded-xl bg-card shadow-card px-5 py-4 text-lg font-semibold active:bg-line"
        >
          Сменные отчёты и корректировки
        </Link>
        <Link
          to="/schedule"
          className="rounded-xl bg-card shadow-card px-5 py-4 text-lg font-semibold active:bg-line"
        >
          График смен
        </Link>
        <Link
          to="/admin/absences"
          className="rounded-xl bg-card shadow-card px-5 py-4 text-lg font-semibold active:bg-line"
        >
          Отсутствия и срывы смен
        </Link>
        <Link
          to="/absence"
          className="rounded-xl bg-card shadow-card px-5 py-4 text-lg font-semibold active:bg-line"
        >
          Записать невыход
        </Link>
        <Link
          to="/admin/missing"
          className="rounded-xl bg-card shadow-card px-5 py-4 text-lg font-semibold active:bg-line"
        >
          Несданные смены
        </Link>
        <Link
          to="/admin/employees"
          className="rounded-xl bg-card shadow-card px-5 py-4 text-lg font-semibold active:bg-line"
        >
          Сотрудники, PIN и допуски
        </Link>
        <Link
          to="/admin/attendance"
          className="rounded-xl bg-card shadow-card px-5 py-4 text-lg font-semibold active:bg-line"
        >
          Явка на экскурсию и стажировку
        </Link>
        <Link
          to="/admin/interview-slots"
          className="rounded-xl bg-card shadow-card px-5 py-4 text-lg font-semibold active:bg-line"
        >
          Слоты собеседований
        </Link>
        <Link
          to="/shipments"
          className="rounded-xl bg-card shadow-card px-5 py-4 text-lg font-semibold active:bg-line"
        >
          Отгрузка и возвраты
        </Link>
        <Link
          to="/receipts"
          className="rounded-xl bg-card shadow-card px-5 py-4 text-lg font-semibold active:bg-line"
        >
          Приёмка сырья
        </Link>
      </nav>
    </div>
  );
}
